import type { CSSProperties } from 'react'
import { Skeleton } from '@vnetwork/datacentral-ui/primitives'

interface TableSkeletonProps {
    /** Number of body rows. @default 5 */
    rows?: number
    /** Number of columns. @default 4 */
    columns?: number
    /** Render a header row. @default true */
    showHeader?: boolean
    className?: string
}

const ROW: CSSProperties = {
    display: 'grid',
    alignItems: 'center',
    gap: 'var(--vn-space-4)',
    padding: 'var(--vn-space-3) var(--vn-space-4)',
}

/**
 * Loading placeholder for tables — built only from DS `Skeleton` + spacing tokens.
 * Used by `useQueryWithHandler` (placeholderData: true) and table sections.
 */
export function TableSkeleton({
    rows = 5,
    columns = 4,
    showHeader = true,
    className,
}: Readonly<TableSkeletonProps>) {
    const grid: CSSProperties = {
        ...ROW,
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    }
    const cellWidth = (col: number) =>
        col === 0 ? '40%' : col === columns - 1 ? '50%' : '100%'

    return (
        <div
            className={className}
            role="status"
            aria-busy="true"
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--vn-space-1)',
            }}
        >
            {showHeader && (
                <div style={grid}>
                    {Array.from({ length: columns }).map((_, i) => (
                        <Skeleton key={i} variant="text" width="60%" />
                    ))}
                </div>
            )}
            {Array.from({ length: rows }).map((_, r) => (
                <div key={r} style={grid}>
                    {Array.from({ length: columns }).map((_, c) => (
                        <Skeleton key={c} height={16} width={cellWidth(c)} />
                    ))}
                </div>
            ))}
        </div>
    )
}
